/** @jsxImportSource @emotion/react */
import { useAtomValue } from 'jotai';
import { Suspense, useMemo } from 'react';
import { Divider, Stack, Typography, styled } from '@mui/material';
import { css } from '@emotion/react';
import { endOfDay } from 'date-fns';
import { companyStateJ, companyStateLoadable } from '../../../services/state/CompanyStateJ';
import { IPersonDataModel, IWorkExperienceDataModel } from '../../../data-models/person.data-model';
import { PersonData } from '../../CompanyProfiles/Summary/components/People/PersonData';
import { PermissionGatedWrapper } from '../../../components/Fallback/PermissionGatedWrapper';
import { getFormattedDuration } from '../../CompanyProfiles/utils/workHistoryUtils';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { CompanyLogoAndName } from '../../../components/grid-renderers/CompanyCellRenderer';
import { formatExternalLink } from '../../../util/url-utils';
import { NoDataMessage2 } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { usePeopleBreakdown } from '../utils/usePeopleBreakdown';
import { TypographyWithTooltip } from '../../../components/EllipsisText/EllipsisText';
import { SecondaryForbiddenMessage } from './constants/forbidden-messages.constants';
import { SummarySection } from './SummarySection';
import { useShowSummaryV2 } from './useShowSummaryV2';

export const DetailedPersonDataContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1.5rem;
`;

const leadershipContainer = css`
  display: grid;
  align-items: start;
  height: 100%;
`;

export function LeadershipPanel({ companyId }: { companyId: number }) {
  return (
    <PermissionGatedWrapper
      permissions={[PermissionKey.canViewMetrics]}
      fallbackMessage={SecondaryForbiddenMessage}
    >
      <SummarySection title={'Leadership'}>
        <Suspense
          fallback={
            <Typography variant='body2' color='text.secondary'>
              Loading...
            </Typography>
          }
        >
          <LeadershipPanelContent companyId={companyId} />
        </Suspense>
      </SummarySection>
    </PermissionGatedWrapper>
  );
}

function LeadershipPanelContent({ companyId }: { companyId: number }) {
  const company = useAtomValue(companyStateJ(companyId));
  const isPortco = company?.companyType === 'portfolio';
  const { getLeaders, getRelationships } = usePeopleBreakdown(companyId);

  const leaders = useMemo(() => getLeaders(), [getLeaders]);
  const relationships = useMemo(() => {
    if (!isPortco) {
      return [];
    }
    return getRelationships().slice(0, 4);
  }, [getRelationships, isPortco]);

  let maxLeaders = 1;
  if (isPortco) {
    if (relationships.length === 0) {
      maxLeaders = 3;
    } else if (relationships.length < 3) {
      maxLeaders = 2;
    }
  }

  if (!leaders || leaders.length === 0) {
    return <NoDataMessage2 />;
  }

  return (
    <div css={leadershipContainer}>
      <DetailedPersonDataContainer>
        {leaders.slice(0, maxLeaders).map((person) => (
          <DetailedPersonData person={person} key={person.id} companyId={companyId} />
        ))}
      </DetailedPersonDataContainer>
      {relationships.length > 0 && isPortco && (
        <Stack alignSelf={'self-end'} mb={'1rem'}>
          <Divider style={{ gridColumn: '1/-1', margin: '1.5rem 0 0.75rem' }} />
          <Typography mb='0.5rem'>Relationships</Typography>
          <Relationships people={relationships} companyId={companyId} />
        </Stack>
      )}
    </div>
  );
}

export function Relationships({ people, companyId }: { people: IPersonDataModel[]; companyId: number }) {
  return (
    <Stack
      display={'grid'}
      gridTemplateColumns={'1fr 1fr'}
      columnGap={'1rem'}
      rowGap={'2rem'}
      alignContent={'stretch'}
    >
      {people.map((p) => (
        <PersonData person={p} key={p.id} avatarSize='medium' companyId={companyId} />
      ))}
    </Stack>
  );
}

export function DetailedPersonData({ person, companyId }: { person: IPersonDataModel; companyId: number }) {
  const showSummaryV2 = useShowSummaryV2();

  return (
    <>
      <PersonData person={person} companyId={companyId} besideAvatar={showSummaryV2} />
      <WorkHistory workExperience={person.workExperience?.slice(0, 3) ?? []} />
    </>
  );
}

/**
 * V2's company column is a fixed track, not `auto`. Each person renders their own grid, so an `auto`
 * track sizes to that person's longest company name and the role column lands somewhere different
 * for every person — the misalignment the design does not have. A fixed width keeps the role column
 * in the same place down the card, at the cost of ellipsizing very long company names.
 *
 * 9rem rather than the design's 100px: names like "Brunswick Group" ellipsize at that width.
 */
const WorkHistoryV2Columns = '9rem minmax(0, 1fr)';

export function WorkHistory({ workExperience }: { workExperience: IWorkExperienceDataModel[] }) {
  const showSummaryV2 = useShowSummaryV2();

  return (
    <PermissionGatedWrapper
      permissions={[PermissionKey.canViewMetrics]}
      fallbackMessage={SecondaryForbiddenMessage}
    >
      <Stack
        display={'grid'}
        gridTemplateColumns={showSummaryV2 ? WorkHistoryV2Columns : 'auto 1fr auto'}
        alignItems={'center'}
        columnGap={'1rem'}
        rowGap={'0.4rem'}
        alignContent={'start'}
      >
        <Typography variant='caption' color='text.secondary' gridColumn={'1/-1'}>
          {showSummaryV2 ? 'Prior Role' : 'Recent Work History'}
        </Typography>
        {workExperience.map((workExperience) => (
          <WorkHistoryItem
            workHistory={workExperience}
            key={`${workExperience.orgName}-${workExperience.startDate}-${workExperience.endDate}`}
          />
        ))}
      </Stack>
    </PermissionGatedWrapper>
  );
}

const today = endOfDay(new Date());
export function WorkHistoryItem({ workHistory }: { workHistory: IWorkExperienceDataModel }) {
  const showSummaryV2 = useShowSummaryV2();
  const { companyId, orgName, startDate, endDate, title } = workHistory;
  const duration = startDate
    ? getFormattedDuration(new Date(startDate), endDate ? new Date(endDate) : today)
    : '';
  const companyLoadable = useAtomValue(companyStateLoadable(companyId ?? -1));
  const company = companyLoadable.state === 'hasData' ? companyLoadable.data : undefined;
  const website = company?.website ? formatExternalLink(company.website) : '';

  const companyProps = {
    name: orgName ?? '',
    logoUrl: company?.logoUrl ?? '',
    url: website,
    avatarProps: {
      size: 'small' as const,
    },
  };
  if (showSummaryV2) {
    // The design carries the tenure inside the role's parentheses rather than in a third column,
    // which is the other half of keeping these rows aligned across people.
    const role = [title ?? '', duration ? `(${duration})` : ''].filter(Boolean).join(' ');

    return (
      <>
        {/* The design colours every company name, not just the ones that resolve to a website —
            `CompanyLogoAndName` only reaches for the link colour when it has a `url`. */}
        <CompanyLogoAndName {...companyProps} typographyProps={{ color: 'secondary' }} />
        <TypographyWithTooltip variant='body2' color='text.primary' noWrap>
          {role}
        </TypographyWithTooltip>
      </>
    );
  }

  return (
    <>
      <CompanyLogoAndName {...companyProps} />
      <TypographyWithTooltip variant='body2' color='text.primary' noWrap>
        {title ?? ''}
      </TypographyWithTooltip>
      <Typography variant='body2' color='text.primary'>
        {duration}
      </Typography>
    </>
  );
}
